import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatAPI } from '../api/axios';

const PromptManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('agent-prompts');
  const [selectedAgent, setSelectedAgent] = useState('general_response');
  const [agentPrompts, setAgentPrompts] = useState({});
  const [commonPrompts, setCommonPrompts] = useState([]);
  const [tones, setTones] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [newPrompt, setNewPrompt] = useState({
    name: '',
    description: '',
    prompt_text: ''
  });
  const [selectedTone, setSelectedTone] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [viewFullPrompt, setViewFullPrompt] = useState(null);
  const [arabicMode, setArabicMode] = useState(false);

  const agents = [
    { id: 'general_response_agent', name: 'General Response Agent' },
    { id: 'flight_booking_agent', name: 'Flight Booking Agent' },
    { id: 'image_analysis_agent', name: 'Image Analysis Agent' },
    { id: 'image_display_agent', name: 'Image Display Agent' }
  ];

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const fetchAgentPrompts = useCallback(async () => {
    try {
      const response = await chatAPI.getAllPrompts();
      const data = response.data;
      setAgentPrompts(data.agent_prompts || {});
      setCommonPrompts(Object.values(data.common_prompts?.prompts || {}));
      const tonesData = data.tone_of_voice?.tones || {};
      setTones(tonesData);
    } catch (error) {
      showMessage('Error fetching prompts', 'error');
    }
  }, []);

  const fetchCommonPrompts = useCallback(async () => {
    try {
      // Mock data for now
      const mockData = [
        {
          id: 1,
          name: 'Common Instructions',
          description: 'General instructions for all agents',
          prompt_text: 'Always be polite and professional. Provide accurate information about Jazeera Airways services.',
          is_active: true
        }
      ];
      setCommonPrompts(mockData);
    } catch (error) {
      showMessage('Error fetching common prompts', 'error');
    }
  }, []);

  const fetchTones = useCallback(async () => {
    try {
      // Mock data for now
      const mockData = [
        {
          id: 1,
          name: 'Professional',
          description: 'Professional and courteous tone',
          instructions: 'Maintain a professional, helpful, and courteous tone in all interactions.',
          is_active: true
        },
        {
          id: 2,
          name: 'Friendly',
          description: 'Warm and friendly tone',
          instructions: 'Use a warm, friendly, and approachable tone while remaining professional.',
          is_active: false
        }
      ];
      setTones(mockData);
    } catch (error) {
      showMessage('Error fetching tones', 'error');
    }
  }, []);

  const handleSavePrompt = async () => {
    try {
      const promptData = {
        name: newPrompt.name,
        description: newPrompt.description,
        prompt_text: newPrompt.prompt_text,
        is_active: false
      };
      
      const requestData = {
        action: newPrompt.id ? 'update' : 'create',
        prompt_name: newPrompt.name.toLowerCase().replace(/\s+/g, '_'),
        prompt_data: promptData
      };
      
      if (activeTab === 'agent-prompts') {
        requestData.agent_name = selectedAgent;
        await chatAPI.saveAgentPrompt(requestData);
      } else {
        await chatAPI.saveCommonPrompt(requestData);
      }
      
      showMessage('Prompt saved successfully', 'success');
      setNewPrompt({ name: '', description: '', prompt_text: '' });
      setEditMode(false);
      fetchAgentPrompts();
    } catch (error) {
      showMessage('Error saving prompt', 'error');
    }
  };

  const handleSaveTone = async () => {
    try {
      const requestData = {
        action: selectedTone.key ? 'update' : 'create',
        tone_name: selectedTone.key || selectedTone.name.toLowerCase().replace(/\s+/g, '_'),
        tone_data: {
          name: selectedTone.name,
          description: selectedTone.description,
          instructions: selectedTone.instructions,
          is_active: false
        }
      };
      
      await chatAPI.saveTone(requestData);
      
      showMessage('Tone saved successfully', 'success');
      setSelectedTone(null);
      setEditMode(false);
      fetchAgentPrompts();
    } catch (error) {
      showMessage('Error saving tone', 'error');
    }
  };

  const handleActivatePrompt = async (promptName, type) => {
    try {
      const requestData = {
        action: 'activate',
        prompt_name: promptName
      };
      
      if (type === 'agent') {
        requestData.agent_name = selectedAgent;
        await chatAPI.saveAgentPrompt(requestData);
      } else {
        await chatAPI.saveCommonPrompt(requestData);
      }
      
      showMessage(`${type} prompt activated successfully`, 'success');
      fetchAgentPrompts();
    } catch (error) {
      showMessage('Error activating prompt', 'error');
    }
  };

  const handleActivateTone = async (toneName) => {
    try {
      await chatAPI.saveTone({
        action: 'activate',
        tone_name: toneName
      });
      
      showMessage(`Tone "${toneName}" activated successfully`, 'success');
      fetchAgentPrompts();
    } catch (error) {
      showMessage('Error activating tone', 'error');
    }
  };
  
  const handleDeletePrompt = async (promptName, type) => {
    if (!window.confirm('Delete this prompt?')) return;
    
    try {
      const requestData = {
        action: 'delete',
        prompt_name: promptName.toLowerCase().replace(/\s+/g, '_')
      };
      
      if (type === 'agent') {
        requestData.agent_name = selectedAgent;
        await chatAPI.saveAgentPrompt(requestData);
      } else {
        await chatAPI.saveCommonPrompt(requestData);
      }
      
      showMessage('Prompt deleted successfully', 'success');
      fetchAgentPrompts();
    } catch (error) {
      showMessage('Error deleting prompt', 'error');
    }
  };
  
  const handleDeleteTone = async (toneName) => {
    if (!window.confirm('Delete this tone?')) return;
    
    try {
      await chatAPI.saveTone({
        action: 'delete',
        tone_name: toneName.toLowerCase().replace(/\s+/g, '_')
      });
      
      showMessage('Tone deleted successfully', 'success');
      fetchAgentPrompts();
    } catch (error) {
      showMessage('Error deleting tone', 'error');
    }
  };

  const renderPromptCard = (prompt, type) => {
    const isActive = prompt.is_active;
    
    return (
      <div 
        key={prompt.id}
        style={{ 
          border: isActive ? '2px solid #004a97' : '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          backgroundColor: isActive ? '#f0f7ff' : 'white'
        }}
      >
        <h4 style={{ margin: '0 0 8px', fontSize: '16px', color: '#2d3748' }}>{prompt.name}</h4>
        <p style={{ color: '#718096', margin: '0 0 12px', fontSize: '14px' }}>{prompt.description}</p>
        
        {viewFullPrompt === prompt.id ? (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '4px',
            maxHeight: '300px',
            overflow: 'auto',
            marginBottom: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px', fontFamily: 'inherit' }}>
              {prompt.prompt_text}
            </pre>
            <button 
              onClick={() => setViewFullPrompt(null)}
              style={{
                marginTop: '8px',
                padding: '4px 8px',
                backgroundColor: '#e2e8f0',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Hide Full Prompt
            </button>
          </div>
        ) : (
          <div style={{ 
            padding: '12px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '4px',
            maxHeight: '80px',
            overflow: 'hidden',
            marginBottom: '12px',
            position: 'relative',
            border: '1px solid #e2e8f0'
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px', fontFamily: 'inherit' }}>
              {prompt.prompt_text.substring(0, 150)}...
            </pre>
            <button 
              onClick={() => setViewFullPrompt(prompt.id)}
              style={{
                position: 'absolute',
                bottom: '4px',
                right: '4px',
                padding: '4px 8px',
                backgroundColor: '#004a97',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '10px'
              }}
            >
              View Full
            </button>
          </div>
        )}
        
        <div style={{ display: 'flex', gap: '8px' }}>
          {!isActive && (
            <button 
              onClick={() => handleActivatePrompt(Object.keys(agentPrompts[selectedAgent]?.prompts || {}).find(key => agentPrompts[selectedAgent].prompts[key] === prompt) || prompt.name, type)}
              style={{
                padding: '6px 12px',
                backgroundColor: '#004a97',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Activate
            </button>
          )}
          <button 
            onClick={() => {
              setNewPrompt(prompt);
              setEditMode(true);
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: 'white',
              color: '#004a97',
              border: '1px solid #004a97',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Edit
          </button>
          {!isActive && (
            <button 
              onClick={() => handleDeletePrompt(prompt.name, type)}
              style={{
                padding: '6px 12px',
                backgroundColor: 'white',
                color: '#ef4444',
                border: '1px solid #ef4444',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Delete
            </button>
          )}
        </div>
        
        {isActive && (
          <div style={{ 
            marginTop: '8px', 
            padding: '4px 8px', 
            backgroundColor: '#dbeafe', 
            color: '#1d4ed8',
            borderRadius: '4px',
            display: 'inline-block',
            fontSize: '12px'
          }}>
            Currently Active
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    fetchAgentPrompts();
    fetchCommonPrompts();
    fetchTones();
  }, [fetchAgentPrompts, fetchCommonPrompts, fetchTones]);

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f0f4f8',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        background: 'linear-gradient(90deg, #004a97 0%, #0066cc 100%)',
        padding: '20px 30px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            height: '40px',
            width: '120px',
            marginRight: '15px',
            backgroundColor: '#004a97',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '16px',
            borderRadius: '4px'
          }}>
            J9 AI Assistant
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Prompt Management</h1>
            <p style={{ margin: '5px 0 0', opacity: 0.8 }}>Customize agent prompts and tone of voice</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/admin/controlpanel')}
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s ease'
          }}
        >
          ← Back to Admin
        </button>
      </div>

      <div style={{ 
        flex: 1, 
        padding: '30px',
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%'
      }}>
        {message && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '20px',
            borderRadius: '8px',
            backgroundColor: messageType === 'error' ? '#fee2e2' : '#d1fae5',
            color: messageType === 'error' ? '#b91c1c' : '#047857',
            border: `1px solid ${messageType === 'error' ? '#fecaca' : '#a7f3d0'}`,
          }}>
            {message}
          </div>
        )}

        {/* Arabic Toggle */}
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>Arabic Mode:</span>
            <div 
              onClick={() => setArabicMode(!arabicMode)}
              style={{
                width: '50px',
                height: '26px',
                backgroundColor: arabicMode ? '#007bff' : '#ccc',
                borderRadius: '13px',
                position: 'relative',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease',
                border: '2px solid #fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  position: 'absolute',
                  top: '1px',
                  left: arabicMode ? '27px' : '1px',
                  transition: 'left 0.3s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }}
              />
            </div>
            <span style={{ 
              fontSize: '12px', 
              color: arabicMode ? '#007bff' : '#666',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}>
              {arabicMode ? (
                <><span>🇸🇦</span> عربي</>
              ) : (
                <><span>🇺🇸</span> EN</>
              )}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          marginBottom: '20px',
          borderBottom: '1px solid #e2e8f0',
          direction: arabicMode ? 'rtl' : 'ltr'
        }}>
          <button
            onClick={() => setActiveTab('agent-prompts')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'agent-prompts' ? '#004a97' : 'transparent',
              color: activeTab === 'agent-prompts' ? 'white' : '#4a5568',
              border: 'none',
              borderBottom: activeTab === 'agent-prompts' ? '3px solid #ffd966' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'agent-prompts' ? 'bold' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            {arabicMode ? 'نماذج الوكيل' : 'Agent Prompts'}
          </button>
          <button
            onClick={() => setActiveTab('common-prompts')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'common-prompts' ? '#004a97' : 'transparent',
              color: activeTab === 'common-prompts' ? 'white' : '#4a5568',
              border: 'none',
              borderBottom: activeTab === 'common-prompts' ? '3px solid #ffd966' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'common-prompts' ? 'bold' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            {arabicMode ? 'النماذج العامة' : 'Common Prompts'}
          </button>
          <button
            onClick={() => setActiveTab('tone-of-voice')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'tone-of-voice' ? '#004a97' : 'transparent',
              color: activeTab === 'tone-of-voice' ? 'white' : '#4a5568',
              border: 'none',
              borderBottom: activeTab === 'tone-of-voice' ? '3px solid #ffd966' : '3px solid transparent',
              cursor: 'pointer',
              fontWeight: activeTab === 'tone-of-voice' ? 'bold' : 'normal',
              transition: 'all 0.2s ease'
            }}
          >
            {arabicMode ? 'نبرة الصوت' : 'Tone of Voice'}
          </button>
        </div>

        {/* Agent Prompts Tab */}
        {activeTab === 'agent-prompts' && (
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#2d3748' }}>Agent Prompts</h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Select Agent:</label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              >
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>{agent.name}</option>
                ))}
              </select>
            </div>

            <h4 style={{ margin: '0 0 16px', fontSize: '16px' }}>{agents.find(a => a.id === selectedAgent)?.name} Prompts</h4>

            {Object.values(agentPrompts[selectedAgent]?.prompts || {}).map((prompt) => renderPromptCard(prompt, 'agent'))}

            {!editMode && (
              <button
                onClick={() => {
                  setNewPrompt({ name: '', description: '', prompt_text: '' });
                  setEditMode(true);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#004a97',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                + Add New Prompt
              </button>
            )}
          </div>
        )}

        {/* Common Prompts Tab */}
        {activeTab === 'common-prompts' && (
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#2d3748' }}>Common Prompts</h3>

            {commonPrompts.map((prompt) => renderPromptCard(prompt, 'common'))}

            {!editMode && (
              <button
                onClick={() => {
                  setNewPrompt({ name: '', description: '', prompt_text: '' });
                  setEditMode(true);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#004a97',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                + Add New Common Prompt
              </button>
            )}
          </div>
        )}

        {/* Tone of Voice Tab */}
        {activeTab === 'tone-of-voice' && (
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#2d3748' }}>Tone of Voice</h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {Object.entries(tones).map(([toneKey, tone]) => (
                <div 
                  key={toneKey}
                  style={{ 
                    border: tone.is_active ? '2px solid #004a97' : '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: tone.is_active ? '#f0f7ff' : 'white'
                  }}
                >
                  <h4 style={{ margin: '0 0 8px', fontSize: '16px', color: '#2d3748' }}>{tone.name}</h4>
                  <p style={{ color: '#718096', margin: '0 0 12px', fontSize: '14px' }}>{tone.description}</p>
                  <p style={{ margin: '0 0 16px', fontSize: '14px' }}>{tone.instructions}</p>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {!tone.is_active && (
                      <button 
                        onClick={() => handleActivateTone(toneKey)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#004a97',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Activate
                      </button>
                    )}
                    <button 
                      onClick={() => {
                        setSelectedTone({...tone, key: toneKey});
                        setEditMode(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'white',
                        color: '#004a97',
                        border: '1px solid #004a97',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Edit
                    </button>
                    {!tone.is_active && (
                      <button 
                        onClick={() => handleDeleteTone(toneKey)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'white',
                          color: '#ef4444',
                          border: '1px solid #ef4444',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  
                  {tone.is_active && (
                    <div style={{ 
                      marginTop: '12px', 
                      padding: '6px 12px', 
                      backgroundColor: '#dbeafe', 
                      color: '#1d4ed8',
                      borderRadius: '4px',
                      display: 'inline-block',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>
                      ✓ Currently Active
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!editMode && (
              <button
                onClick={() => {
                  setSelectedTone({ name: '', description: '', instructions: '', is_active: false });
                  setEditMode(true);
                }}
                style={{
                  marginTop: '20px',
                  padding: '8px 16px',
                  backgroundColor: '#004a97',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                + Add New Tone
              </button>
            )}
          </div>
        )}

        {/* Edit Modal */}
        {editMode && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <h3 style={{ margin: '0 0 20px', fontSize: '18px' }}>
                {activeTab === 'tone-of-voice' 
                  ? (selectedTone?.name ? 'Edit Tone' : 'New Tone')
                  : (newPrompt.id ? 'Edit Prompt' : 'New Prompt')
                }
              </h3>

              {activeTab === 'tone-of-voice' ? (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Name</label>
                    <input
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      value={selectedTone?.name || ''}
                      onChange={(e) => setSelectedTone({...selectedTone, name: e.target.value})}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Description</label>
                    <input
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      value={selectedTone?.description || ''}
                      onChange={(e) => setSelectedTone({...selectedTone, description: e.target.value})}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Instructions</label>
                    <textarea
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', minHeight: '100px' }}
                      value={selectedTone?.instructions || ''}
                      onChange={(e) => setSelectedTone({...selectedTone, instructions: e.target.value})}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Name</label>
                    <input
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      value={newPrompt.name}
                      onChange={(e) => setNewPrompt({...newPrompt, name: e.target.value})}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Description</label>
                    <input
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                      value={newPrompt.description}
                      onChange={(e) => setNewPrompt({...newPrompt, description: e.target.value})}
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Prompt Text</label>
                    <textarea
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', minHeight: '200px' }}
                      value={newPrompt.prompt_text}
                      onChange={(e) => setNewPrompt({...newPrompt, prompt_text: e.target.value})}
                    />
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setNewPrompt({ name: '', description: '', prompt_text: '' });
                    setSelectedTone(null);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    color: '#4a5568',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={activeTab === 'tone-of-voice' ? handleSaveTone : handleSavePrompt}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#004a97',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{
        backgroundColor: '#004a97',
        color: '#f8f9fa',
        padding: '20px',
        textAlign: 'center',
        fontSize: '14px',
        marginTop: '40px'
      }}>
        J9 AI Assistant Prompt Management © 2025
      </div>
    </div>
  );
};

export default PromptManagement;